import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const EMPTY_LINE = { label: "", qty: 1, unit_price: "" };
const DEFAULT_MILESTONES = [
    { label: "Acompte à la signature", pct: 30 },
    { label: "Paiement mi-chantier", pct: 40 },
    { label: "Solde à réception", pct: 30 },
];
const STATUS_BADGE = {
    envoye: { l: "Envoyé", c: "border-[#B89C6A] text-[#B89C6A]" },
    signe: { l: "Signé ✓", c: "border-green-600 text-green-700" },
    refuse: { l: "Refusé", c: "border-red-600 text-red-700" },
};

export const AdminProposals = () => {
    const [proposals, setProposals] = useState([]);
    const [form, setForm] = useState({ client_email: "", title: "", tva_pct: 10, notes: "" });
    const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
    const [milestones, setMilestones] = useState(DEFAULT_MILESTONES.map((m) => ({ ...m })));
    const [busy, setBusy] = useState(false);

    const load = () =>
        axios.get(`${API}/admin/proposals`).then((r) => setProposals(r.data.proposals || [])).catch(() => {});

    useEffect(() => { load(); }, []);

    const setLine = (i, k, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
    const totalHt = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
    const tva = totalHt * (Number(form.tva_pct) || 0) / 100;
    const setMilestone = (i, k, v) => setMilestones(milestones.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
    const pctSum = milestones.reduce((s, m) => s + (Number(m.pct) || 0), 0);

    const create = async (e) => {
        e.preventDefault();
        const cleanLines = lines
            .filter((l) => l.label.trim() && Number(l.unit_price) >= 0 && Number(l.qty) > 0)
            .map((l) => ({ label: l.label.trim(), qty: Number(l.qty), unit_price: Number(l.unit_price) }));
        if (cleanLines.length === 0) { toast.error("Ajoutez au moins une ligne valide"); return; }
        if (Math.abs(pctSum - 100) > 0.01) { toast.error(`La somme des paliers doit faire 100 % (actuellement ${pctSum} %)`); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/admin/proposals`, {
                client_email: form.client_email,
                title: form.title,
                tva_pct: Number(form.tva_pct),
                notes: form.notes || null,
                lines: cleanLines,
                milestones: milestones.map((m) => ({ label: m.label, pct: Number(m.pct) })),
            });
            setForm({ client_email: "", title: "", tva_pct: 10, notes: "" });
            setLines([{ ...EMPTY_LINE }]);
            setMilestones(DEFAULT_MILESTONES.map((m) => ({ ...m })));
            toast.success("Devis envoyé au client par email ✓");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur de création");
        } finally {
            setBusy(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer ce devis ?")) return;
        try {
            await axios.delete(`${API}/admin/proposals/${id}`);
            toast.success("Devis supprimé");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        }
    };

    const requestPayment = async (id) => {
        try {
            const { data } = await axios.post(`${API}/admin/proposals/${id}/request-payment`);
            toast.success(`Appel de paiement envoyé au client (${fmt(data.amount_eur)} €) ✓`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur d'envoi");
        }
    };

    return (
        <div className="space-y-6">
            <form onSubmit={create} className="lux-card p-6 space-y-4">
                <div className="tracking-luxe text-[#B89C6A]">Créer un devis signable en ligne</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <input data-testid="proposal-client-email" className="lux-input" type="email" placeholder="Email du client *" required
                        value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
                    <input data-testid="proposal-title" className="lux-input" placeholder="Intitulé * (ex: Rénovation salle de bain)" required
                        value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <select data-testid="proposal-tva" className="lux-input" value={form.tva_pct}
                        onChange={(e) => setForm({ ...form, tva_pct: e.target.value })}>
                        <option value="10">TVA 10 % (rénovation logement +2 ans)</option>
                        <option value="20">TVA 20 % (neuf / construction)</option>
                        <option value="5.5">TVA 5,5 % (rénovation énergétique)</option>
                    </select>
                </div>

                <div className="space-y-2">
                    {lines.map((l, i) => (
                        <div key={i} className="flex gap-2 items-center">
                            <input data-testid={`proposal-line-label-${i}`} className="lux-input flex-1" placeholder="Désignation (ex: Dépose et évacuation)"
                                value={l.label} onChange={(e) => setLine(i, "label", e.target.value)} />
                            <input data-testid={`proposal-line-qty-${i}`} className="lux-input w-20" type="number" min="0.1" step="0.1" placeholder="Qté"
                                value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} />
                            <input data-testid={`proposal-line-price-${i}`} className="lux-input w-28" type="number" min="0" step="0.01" placeholder="PU HT €"
                                value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                            {lines.length > 1 && (
                                <button type="button" onClick={() => setLines(lines.filter((_, idx) => idx !== i))}
                                    className="text-red-700/70 hover:text-red-700 px-1">✕</button>
                            )}
                        </div>
                    ))}
                    <button data-testid="proposal-add-line" type="button" onClick={() => setLines([...lines, { ...EMPTY_LINE }])}
                        className="text-[11px] tracking-wider uppercase underline hover:text-[#B89C6A]">
                        + Ajouter une ligne
                    </button>
                </div>

                <textarea data-testid="proposal-notes" className="lux-input w-full" rows={2} placeholder="Notes / conditions (facultatif)"
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

                <div className="border hairline p-4">
                    <div className="flex justify-between items-center flex-wrap gap-2 mb-3">
                        <span className="tracking-luxe text-[#B89C6A] text-[10px]">Échéancier de paiement</span>
                        <span data-testid="milestones-sum" className={`text-[11px] tracking-wider ${Math.abs(pctSum - 100) > 0.01 ? "text-red-600" : "text-green-700"}`}>
                            Total : {pctSum} % {Math.abs(pctSum - 100) > 0.01 ? "≠ 100 %" : "✓"}
                        </span>
                    </div>
                    <div className="space-y-2">
                        {milestones.map((m, i) => (
                            <div key={i} className="flex gap-2 items-center">
                                <input data-testid={`milestone-label-${i}`} className="lux-input flex-1" placeholder="Libellé du palier"
                                    value={m.label} onChange={(e) => setMilestone(i, "label", e.target.value)} />
                                <div className="flex items-center gap-1">
                                    <input data-testid={`milestone-pct-${i}`} className="lux-input w-20 text-right" type="number" min="1" max="100" step="1"
                                        value={m.pct} onChange={(e) => setMilestone(i, "pct", e.target.value)} />
                                    <span className="text-[12px] text-[#1C1E1D]/60">%</span>
                                </div>
                                <span className="text-[11px] text-[#B89C6A] w-24 text-right whitespace-nowrap">
                                    {fmt((totalHt + tva) * (Number(m.pct) || 0) / 100)} €
                                </span>
                                {milestones.length > 1 && (
                                    <button type="button" onClick={() => setMilestones(milestones.filter((_, idx) => idx !== i))}
                                        className="text-red-700/70 hover:text-red-700 px-1">✕</button>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-4 mt-3">
                        {milestones.length < 6 && (
                            <button data-testid="milestone-add" type="button" onClick={() => setMilestones([...milestones, { label: "", pct: 0 }])}
                                className="text-[11px] tracking-wider uppercase underline hover:text-[#B89C6A]">
                                + Ajouter un palier
                            </button>
                        )}
                        <button type="button" onClick={() => setMilestones(DEFAULT_MILESTONES.map((m) => ({ ...m })))}
                            className="text-[11px] tracking-wider uppercase underline text-[#1C1E1D]/50 hover:text-[#B89C6A]">
                            Rétablir 30/40/30
                        </button>
                    </div>
                </div>

                <div className="flex justify-between items-center flex-wrap gap-3">
                    <div className="text-[13px] text-[#1C1E1D]/70">
                        Total HT : <b>{fmt(totalHt)} €</b> · TVA : {fmt(tva)} € ·{" "}
                        <span className="font-serif-lux text-lg text-[#B89C6A]">TTC : {fmt(totalHt + tva)} €</span>
                    </div>
                    <button data-testid="proposal-create" type="submit" disabled={busy} className="btn-champagne disabled:opacity-50">
                        {busy ? "Envoi…" : "Créer et envoyer au client"}
                    </button>
                </div>
            </form>

            <div className="space-y-3">
                {proposals.map((p) => {
                    const badge = STATUS_BADGE[p.status] || STATUS_BADGE.envoye;
                    return (
                        <div key={p.id} data-testid={`admin-proposal-${p.id}`} className="lux-card p-5 flex justify-between items-start gap-3 flex-wrap">
                            <div className="min-w-0">
                                <div className="tracking-luxe text-[#B89C6A] text-[10px]">{p.number} · {p.client_email}</div>
                                <div className="font-serif-lux text-lg mt-1">{p.title}</div>
                                <div className="text-[13px] text-[#1C1E1D]/70 mt-1">
                                    {fmt(p.total_ttc)} € TTC · {p.lines.length} ligne{p.lines.length > 1 ? "s" : ""} · valable jusqu'au {p.valid_until}
                                </div>
                                {p.status === "signe" && p.signature && (
                                    <div className="text-[12px] text-green-700 mt-1">Signé par {p.signature.name} le {p.signature.signed_at?.slice(0, 10)}</div>
                                )}
                                {p.status === "signe" && p.payment_schedule && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {p.payment_schedule.map((m) => (
                                            <span key={m.key}
                                                className={`text-[10px] tracking-wider uppercase px-2 py-1 border ${m.paid ? "border-green-600 text-green-700" : "hairline text-[#1C1E1D]/50"}`}>
                                                {m.paid ? "✓ " : ""}{m.label} · {fmt(m.amount_eur)} €
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                                <span className={`text-[10px] tracking-wider uppercase px-3 py-1 border ${badge.c}`}>{badge.l}</span>
                                {p.status === "signe" && !p.fully_paid && (
                                    <button data-testid={`admin-request-payment-${p.id}`} onClick={() => requestPayment(p.id)}
                                        className="text-[11px] tracking-wider uppercase underline hover:text-[#B89C6A]">
                                        💶 Appeler le paiement
                                    </button>
                                )}
                                {p.status !== "signe" && (
                                    <button data-testid={`admin-proposal-delete-${p.id}`} onClick={() => del(p.id)}
                                        className="text-[11px] tracking-wider uppercase text-red-700/70 hover:text-red-700 underline">
                                        Supprimer
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
                {proposals.length === 0 && <p className="text-[13px] text-[#1C1E1D]/50 italic">Aucun devis envoyé pour l'instant.</p>}
            </div>
        </div>
    );
};
